// Meowdoku — 4x4 sudoku with cats. Four symbols, four rows, four columns, four 2x2 boxes.
//
// 4x4 rather than 9x9 because this gets played one-handed on a phone between games of
// Tap-Tap, and a 9x9 is a sit-down-with-a-pencil affair.
//
// Kept pure and out of extrafun.tsx so the generator can be tested — a puzzle with no
// solution, or with two, is not obviously broken until somebody is stuck on it.

/** 0 means empty; 1..4 are the cats. */
export type Cell = 0 | 1 | 2 | 3 | 4
export type Grid = Cell[]

export const SIZE = 4
export const CELLS = SIZE * SIZE

/** Index order matches Cell values 1..4. */
export const CATS = ['😺', '😻', '😾', '🙀'] as const

export type Rng = () => number

/** The 12 groups every solved grid must satisfy: 4 rows, 4 columns, 4 boxes. */
export function groups(): number[][] {
  const out: number[][] = []
  for (let r = 0; r < SIZE; r++) out.push([0, 1, 2, 3].map((c) => r * SIZE + c))
  for (let c = 0; c < SIZE; c++) out.push([0, 1, 2, 3].map((r) => r * SIZE + c))
  for (let br = 0; br < 2; br++) {
    for (let bc = 0; bc < 2; bc++) {
      const box: number[] = []
      for (let r = 0; r < 2; r++) {
        for (let c = 0; c < 2; c++) box.push((br * 2 + r) * SIZE + (bc * 2 + c))
      }
      out.push(box)
    }
  }
  return out
}

const GROUPS = groups()

/** Cells that break a row, column or box rule. Empty cells are never conflicts. */
export function conflicts(grid: Grid): Set<number> {
  const bad = new Set<number>()
  for (const group of GROUPS) {
    const seen = new Map<Cell, number[]>()
    for (const i of group) {
      const v = grid[i] ?? 0
      if (v === 0) continue
      const list = seen.get(v) ?? []
      list.push(i)
      seen.set(v, list)
    }
    for (const list of seen.values()) {
      if (list.length > 1) for (const i of list) bad.add(i)
    }
  }
  return bad
}

export function isSolved(grid: Grid): boolean {
  if (grid.length !== CELLS) return false
  if (grid.some((v) => v === 0)) return false
  return conflicts(grid).size === 0
}

/** 0 -> 1 -> 2 -> 3 -> 4 -> 0. One tap per cat, and tapping past the last clears. */
export function cycle(value: Cell): Cell {
  return ((value + 1) % (SIZE + 1)) as Cell
}

function candidates(grid: Grid, index: number): Cell[] {
  const taken = new Set<Cell>()
  for (const group of GROUPS) {
    if (!group.includes(index)) continue
    for (const i of group) {
      const v = grid[i] ?? 0
      if (v !== 0) taken.add(v as Cell)
    }
  }
  return ([1, 2, 3, 4] as Cell[]).filter((v) => !taken.has(v))
}

/**
 * How many ways the grid can be completed, counting no further than `limit`.
 * Used to guarantee a generated puzzle has exactly one answer.
 */
export function countSolutions(grid: Grid, limit = 2): number {
  const work = [...grid]
  let found = 0

  const step = (): void => {
    if (found >= limit) return
    const i = work.findIndex((v) => v === 0)
    if (i === -1) {
      found++
      return
    }
    for (const v of candidates(work, i)) {
      work[i] = v
      step()
      work[i] = 0
      if (found >= limit) return
    }
  }

  step()
  return found
}

function shuffle<T>(list: T[], rng: Rng): T[] {
  const out = [...list]
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1))
    const a = out[i] as T
    const b = out[j] as T
    out[i] = b
    out[j] = a
  }
  return out
}

/**
 * A random solved grid.
 *
 * Built by permuting a known-good base rather than by search: relabelling the symbols,
 * swapping rows inside a band, columns inside a stack, and the bands and stacks
 * themselves all preserve every row, column and box rule.
 */
export function generateSolved(rng: Rng = Math.random): Grid {
  const base: Cell[] = [1, 2, 3, 4, 3, 4, 1, 2, 2, 1, 4, 3, 4, 3, 2, 1]

  const symbols = shuffle([1, 2, 3, 4] as Cell[], rng)
  const rowOrder = (shuffle([0, 1], rng) as number[]).flatMap((band) =>
    (shuffle([0, 1], rng) as number[]).map((r) => band * 2 + r),
  )
  const colOrder = (shuffle([0, 1], rng) as number[]).flatMap((stack) =>
    (shuffle([0, 1], rng) as number[]).map((c) => stack * 2 + c),
  )

  const out: Grid = new Array(CELLS).fill(0) as Grid
  for (let r = 0; r < SIZE; r++) {
    for (let c = 0; c < SIZE; c++) {
      const srcRow = rowOrder[r] as number
      const srcCol = colOrder[c] as number
      const v = base[srcRow * SIZE + srcCol] as Cell
      out[r * SIZE + c] = symbols[v - 1] as Cell
    }
  }
  return out
}

export interface Puzzle {
  /** The player's grid, with blanks to fill. */
  start: Grid
  /** Indices the player may not change. */
  given: Set<number>
  solution: Grid
}

/**
 * A puzzle with exactly one solution.
 *
 * Clues are removed one at a time and put back whenever removal would allow a second
 * answer, so the result is always solvable by reasoning rather than guessing.
 */
export function generatePuzzle(rng: Rng = Math.random, targetClues = 6): Puzzle {
  const solution = generateSolved(rng)
  const start = [...solution]

  for (const i of shuffle([...Array(CELLS).keys()], rng)) {
    const remaining = start.filter((v) => v !== 0).length
    if (remaining <= targetClues) break
    const saved = start[i] as Cell
    start[i] = 0
    if (countSolutions(start) !== 1) start[i] = saved
  }

  const given = new Set<number>()
  start.forEach((v, i) => {
    if (v !== 0) given.add(i)
  })
  return { start, given, solution }
}
